import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { b2bImportApollo, b2bCheckApollo } from '../../../lib/adminApi';
import AdminSidebar from '../../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

// ── Accounts CSV column map ───────────────────────────────────────────────────
const ACCT_COL_MAP = {
  'Company Name':        'company_name',
  'Website':             'website',
  '# Employees':         'employee_count',
  'Industry':            'industry',
  'Annual Revenue':      'annual_revenue',
  'Total Funding':       'total_funding',
  'Company City':        'company_city',
  'Company State':       'company_state',
  'Company Country':     'company_country',
  'Company Phone':       'company_phone',
  'Company Linkedin Url':'company_linkedin',
  'Apollo Account Id':   'apollo_org_id',
  'SIC Codes':           'sic_codes',
  'NAICS Codes':         'naics_codes',
  'Technologies':        'technologies',
  'Keywords':            'keywords',
  'Short Description':   'short_description',
  'Founded Year':        'founded_year',
};

// ── Contacts CSV column map ───────────────────────────────────────────────────
const CONTACT_COL_MAP = {
  'First Name':              'first_name',
  'Last Name':               'last_name',
  'Title':                   'title',
  'Company':                 'company_name',
  'Company Name':            'company_name',
  'Email':                   'email',
  'Email Status':            'email_status',
  'Seniority':               'seniority',
  'Departments':             'departments',
  'Work Direct Phone':       'work_phone',
  'Corporate Phone':         'corporate_phone',
  'Mobile Phone':            'mobile_phone',
  'Person Linkedin Url':     'linkedin_url',
  'Website':                 'website',
  'Company Linkedin Url':    'company_linkedin',
  'Company City':            'company_city',
  'Company State':           'company_state',
  'Company Country':         'company_country',
  'Company Phone':           'company_phone',
  'City':                    'city',
  'State':                   'state',
  'Country':                 'country',
  '# Employees':             'employee_count',
  'Industry':                'industry',
  'Annual Revenue':          'annual_revenue',
  'Total Funding':           'total_funding',
  'Apollo Contact Id':       'apollo_id',
  'Apollo Account Id':       'apollo_account_id',
};

function parseCSV(text, colMap) {
  text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows = [];
  let row = [], cell = '', inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') { cell += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      row.push(cell); cell = '';
    } else if (ch === '\n' && !inQuotes) {
      row.push(cell); cell = '';
      rows.push(row); row = [];
    } else {
      cell += ch;
    }
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  if (rows.length < 2) return [];

  const headers = rows[0].map(h => h.trim());
  const result = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    if (r.every(c => !c.trim())) continue;
    const obj = {};
    headers.forEach((h, idx) => {
      const field = colMap[h];
      if (field) obj[field] = (r[idx] || '').trim();
    });
    result.push(obj);
  }
  return result;
}

function cleanNum(val) {
  if (!val) return null;
  const n = parseFloat(String(val).replace(/[^0-9.]/g, ''));
  return isNaN(n) ? null : n;
}

function isAccountsCSV(headers) {
  // Accounts CSV has "Company Name" but no "First Name"
  return headers.includes('Company Name') && !headers.includes('First Name');
}

function detectAndParse(text) {
  const firstLine = text.split('\n')[0];
  const headers = firstLine.split(',').map(h => h.trim().replace(/"/g, ''));
  if (isAccountsCSV(headers)) {
    const rows = parseCSV(text, ACCT_COL_MAP).map(r => ({
      ...r,
      employee_count: cleanNum(r.employee_count),
      annual_revenue: cleanNum(r.annual_revenue),
      total_funding:  cleanNum(r.total_funding),
      founded_year:   cleanNum(r.founded_year),
    }));
    return { type: 'accounts', rows };
  } else {
    const rows = parseCSV(text, CONTACT_COL_MAP).map(r => ({
      ...r,
      employee_count: cleanNum(r.employee_count),
      annual_revenue: cleanNum(r.annual_revenue),
    }));
    return { type: 'contacts', rows };
  }
}

function FileDropZone({ label, hint, onFile, fileName, rowCount }) {
  const ref = useRef();
  return (
    <div style={s.dropZoneWrap}>
      <div style={s.dropLabel}>{label}</div>
      <div style={s.dropHint}>{hint}</div>
      <div style={s.uploadZone} onClick={() => ref.current.click()}>
        <input ref={ref} type="file" accept=".csv" style={{ display: 'none' }}
          onChange={e => { if (e.target.files[0]) onFile(e.target.files[0]); }} />
        {fileName ? (
          <>
            <div style={{ fontSize: 24, marginBottom: 6 }}>📄</div>
            <div style={{ color: NAVY, fontWeight: 600, fontSize: 13 }}>{fileName}</div>
            <div style={{ color: '#059669', fontSize: 12, marginTop: 4 }}>{rowCount} rows ready</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 28, marginBottom: 6 }}>📂</div>
            <div style={{ color: '#64748b', fontSize: 13 }}>Click to select CSV</div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ApolloImport() {
  const navigate = useNavigate();
  const [acctRows,  setAcctRows]  = useState([]);
  const [acctFile,  setAcctFile]  = useState('');
  const [contRows,  setContRows]  = useState([]);
  const [contFile,  setContFile]  = useState('');
  const [importing, setImporting] = useState(false);
  const [checking,  setChecking]  = useState(false);
  const [dupes,     setDupes]     = useState(null);  // { accounts_existing, contacts_existing }
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState('');

  function handleFile(file) {
    setError('');
    setDupes(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const { type, rows } = detectAndParse(ev.target.result);
        if (type === 'accounts') { setAcctRows(rows); setAcctFile(file.name); }
        else                      { setContRows(rows); setContFile(file.name); }
      } catch (e) { setError('Failed to parse CSV: ' + e.message); }
    };
    reader.readAsText(file);
  }

  // Run pre-flight check whenever rows change — send only identifiers, not full rows
  useEffect(() => {
    const total = acctRows.length + contRows.length;
    if (total === 0) { setDupes(null); return; }
    setChecking(true);
    const payload = {
      accounts: acctRows.map(r => ({ apollo_org_id: r.apollo_org_id || '', company_name: r.company_name || '' })),
      contacts: contRows.map(r => ({ apollo_id: r.apollo_id || '', email: r.email || '' })),
    };
    b2bCheckApollo(payload)
      .then(r => { setDupes(r); setChecking(false); })
      .catch(() => { setDupes(null); setChecking(false); }); // silently fail — check is informational only
  }, [acctRows, contRows]);

  const totalRows = acctRows.length + contRows.length;
  const uniqueAccts = new Set([
    ...acctRows.map(r => r.company_name),
    ...contRows.map(r => r.company_name),
  ].filter(Boolean)).size;
  const withEmail = contRows.filter(r => r.email).length;

  async function handleImport() {
    if (totalRows === 0) return;
    setImporting(true);
    setError('');
    try {
      const res = await b2bImportApollo({ accounts: acctRows, contacts: contRows });
      setResult(res);
    } catch (e) { setError(e.message); }
    setImporting(false);
  }

  function reset() { setAcctRows([]); setAcctFile(''); setContRows([]); setContFile(''); setResult(null); setError(''); setDupes(null); }

  return (
    <div style={s.page}>
      <AdminSidebar active="b2b-contacts" />
      <main style={s.main}>
        <button style={s.backBtn} onClick={() => navigate('/admin/b2b/contacts')}>← Contacts</button>

        <div style={s.header}>
          <h1 style={s.title}>Import from Apollo</h1>
          <p style={s.subtitle}>
            Export CSVs from Apollo (Accounts → Export, Contacts → Export), then upload below.
            Upload one or both files — you can import accounts only, contacts only, or both together.
          </p>
        </div>

        <div style={s.infoGrid}>
          <div style={s.infoCard}>
            <div style={s.infoIcon}>🔁</div>
            <div style={s.infoTitle}>Smart De-duplication</div>
            <div style={s.infoBody}>
              Companies matched by <strong>Apollo Account ID</strong> first, then company name. Contacts matched by <strong>Apollo Contact ID</strong> first, then email. Existing records are updated with richer data — never doubled.
            </div>
          </div>
          <div style={s.infoCard}>
            <div style={s.infoIcon}>🤖</div>
            <div style={s.infoTitle}>AI Classification</div>
            <div style={s.infoBody}>
              Every company gets a <strong>Standard Industry</strong> (e.g. Manufacturing, Technology, Healthcare) and a <strong>Size Bucket</strong> (e.g. 201–500). Every contact title gets a <strong>Standard Title</strong> (e.g. CFO, A/P Manager). Used for filtering and targeting.
            </div>
          </div>
          <div style={s.infoCard}>
            <div style={s.infoIcon}>📦</div>
            <div style={s.infoTitle}>What Gets Captured</div>
            <div style={s.infoBody}>
              <strong>Companies:</strong> SIC &amp; NAICS codes, technologies, keywords, short description, founded year, total funding.<br />
              <strong>Contacts:</strong> seniority, department, email verification status, mobile phone.
            </div>
          </div>
        </div>

        {!result && (
          <>
            <div style={s.dropRow}>
              <FileDropZone
                label="Accounts CSV"
                hint="Apollo → Accounts → Export to CSV"
                onFile={handleFile}
                fileName={acctFile}
                rowCount={acctRows.length}
              />
              <FileDropZone
                label="Contacts CSV"
                hint="Apollo → Contacts → Export to CSV"
                onFile={handleFile}
                fileName={contFile}
                rowCount={contRows.length}
              />
            </div>

            {totalRows > 0 && (
              <>
                <div style={s.statsRow}>
                  <div style={s.statCard}>
                    <div style={s.statLabel}>Companies</div>
                    <div style={s.statVal}>{acctRows.length || uniqueAccts}</div>
                  </div>
                  <div style={s.statCard}>
                    <div style={s.statLabel}>Contacts</div>
                    <div style={s.statVal}>{contRows.length}</div>
                  </div>
                  <div style={s.statCard}>
                    <div style={s.statLabel}>With Email</div>
                    <div style={s.statVal}>{withEmail}</div>
                  </div>
                  <div style={s.statCard}>
                    <div style={s.statLabel}>No Email</div>
                    <div style={{ ...s.statVal, color: contRows.length - withEmail > 0 ? '#d97706' : NAVY }}>
                      {contRows.length - withEmail}
                    </div>
                  </div>
                  <div style={{ ...s.statCard, borderColor: checking ? '#e2e8f0' : dupes && (dupes.accounts_existing + dupes.contacts_existing) > 0 ? '#fbbf24' : '#86efac' }}>
                    <div style={s.statLabel}>Already in DB</div>
                    <div style={{ ...s.statVal, color: checking ? '#94a3b8' : dupes && (dupes.accounts_existing + dupes.contacts_existing) > 0 ? '#d97706' : '#059669' }}>
                      {checking ? '…' : dupes ? (dupes.accounts_existing + dupes.contacts_existing) : '—'}
                    </div>
                  </div>
                </div>

                {!checking && dupes && (dupes.accounts_existing + dupes.contacts_existing) > 0 && (
                  <div style={s.dupeWarning}>
                    <strong>⚠ Heads up:</strong> {dupes.contacts_existing > 0 && `${dupes.contacts_existing} contact${dupes.contacts_existing > 1 ? 's' : ''} already in the database`}{dupes.contacts_existing > 0 && dupes.accounts_existing > 0 && ', '}{dupes.accounts_existing > 0 && `${dupes.accounts_existing} company match${dupes.accounts_existing > 1 ? 'es' : ''} found`}. Those records will be skipped (contacts) or updated with richer data (companies).
                  </div>
                )}
                {!checking && dupes && (dupes.accounts_existing + dupes.contacts_existing) === 0 && totalRows > 0 && (
                  <div style={s.clearMsg}>✓ All records are new — no duplicates found.</div>
                )}

                {contRows.length > 0 && (
                  <div style={{ ...s.card, marginBottom: 20 }}>
                    <div style={s.cardHeader}>
                      <span style={s.cardTitle}>Contact Preview (first 8 rows)</span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={s.table}>
                        <thead>
                          <tr>
                            {['Name', 'Title', 'Seniority', 'Company', 'Email', 'Status', 'Location'].map(h => (
                              <th key={h} style={s.th}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {contRows.slice(0, 8).map((r, i) => (
                            <tr key={i} style={s.tr}>
                              <td style={{ ...s.td, fontWeight: 500 }}>{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</td>
                              <td style={{ ...s.td, color: '#64748b', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title || '—'}</td>
                              <td style={{ ...s.td, fontSize: 11 }}>{r.seniority || '—'}</td>
                              <td style={{ ...s.td, color: NAVY }}>{r.company_name || '—'}</td>
                              <td style={{ ...s.td, fontSize: 12 }}>{r.email || <span style={{ color: '#ef4444' }}>no email</span>}</td>
                              <td style={{ ...s.td, fontSize: 11 }}>
                                {r.email_status ? <span style={{ ...s.statusPill, background: r.email_status === 'Verified' ? 'rgba(5,150,105,0.1)' : 'rgba(148,163,184,0.2)', color: r.email_status === 'Verified' ? '#059669' : '#64748b' }}>{r.email_status}</span> : '—'}
                              </td>
                              <td style={{ ...s.td, color: '#64748b', fontSize: 12 }}>{[r.city || r.company_city, r.state || r.company_state].filter(Boolean).join(', ') || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {contRows.length > 8 && (
                      <div style={{ padding: '8px 16px', color: '#94a3b8', fontSize: 12, borderTop: '1px solid #f1f5f9' }}>
                        … and {contRows.length - 8} more rows
                      </div>
                    )}
                  </div>
                )}

                {error && <div style={s.errorBox}>{error}</div>}

                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button style={s.primaryBtn} onClick={handleImport} disabled={importing}>
                    {importing ? 'Importing & classifying…' : `Import ${totalRows} rows`}
                  </button>
                  <button style={s.cancelBtn} onClick={reset}>Clear</button>
                </div>
                {importing && (
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 10 }}>
                    AI is classifying industries and titles — this may take 15–30 seconds for large files.
                  </div>
                )}
              </>
            )}

            {error && totalRows === 0 && <div style={s.errorBox}>{error}</div>}
          </>
        )}

        {result && (
          <div style={s.resultCard}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
            <h2 style={{ color: NAVY, margin: '0 0 24px', fontSize: 20 }}>Import Complete</h2>
            <div style={s.resultGrid}>
              <div style={s.resultStat}>
                <div style={{ ...s.statVal, color: '#059669', fontSize: 28 }}>{result.accounts_created}</div>
                <div style={s.statLabel}>New Companies</div>
              </div>
              <div style={s.resultStat}>
                <div style={{ ...s.statVal, color: BLUE, fontSize: 28 }}>{result.accounts_updated || 0}</div>
                <div style={s.statLabel}>Companies Updated</div>
              </div>
              <div style={s.resultStat}>
                <div style={{ ...s.statVal, color: '#059669', fontSize: 28 }}>{result.contacts_created}</div>
                <div style={s.statLabel}>New Contacts</div>
              </div>
              <div style={s.resultStat}>
                <div style={{ ...s.statVal, color: '#64748b', fontSize: 28 }}>{result.contacts_skipped}</div>
                <div style={s.statLabel}>Contacts Skipped</div>
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div style={{ marginTop: 20, background: 'rgba(239,68,68,0.06)', borderRadius: 8, padding: 16, textAlign: 'left' }}>
                <div style={{ color: '#dc2626', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{result.errors.length} row(s) had errors:</div>
                {result.errors.slice(0, 10).map((e, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}><strong>{e.row}</strong>: {e.error}</div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 28 }}>
              <button style={s.primaryBtn} onClick={() => navigate('/admin/b2b/accounts')}>View Accounts →</button>
              <button style={s.primaryBtn} onClick={() => navigate('/admin/b2b/contacts')}>View Contacts →</button>
              <button style={s.cancelBtn} onClick={reset}>Import Another File</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main: { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  backBtn: { background: 'transparent', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: '0 0 8px', display: 'block' },
  header: { marginBottom: 24 },
  title: { color: NAVY, fontSize: 24, fontWeight: 700, margin: '0 0 8px' },
  subtitle: { color: '#64748b', fontSize: 13, margin: 0, lineHeight: 1.6, maxWidth: 640 },
  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 28 },
  infoCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  infoIcon: { fontSize: 22, marginBottom: 8 },
  infoTitle: { color: NAVY, fontWeight: 700, fontSize: 13, marginBottom: 6 },
  infoBody: { color: '#64748b', fontSize: 12, lineHeight: 1.65 },
  dropRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 },
  dropZoneWrap: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 20px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  dropLabel: { color: NAVY, fontWeight: 700, fontSize: 14, marginBottom: 4 },
  dropHint: { color: '#94a3b8', fontSize: 11, marginBottom: 12 },
  uploadZone: { border: '2px dashed #e2e8f0', borderRadius: 10, padding: '28px 16px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 12 },
  dupeWarning: { background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.4)', borderRadius: 8, color: '#92400e', fontSize: 13, padding: '10px 14px', marginBottom: 16 },
  clearMsg: { color: '#059669', fontSize: 13, fontWeight: 500, marginBottom: 16 },
  statCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px' },
  statLabel: { color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 },
  statVal: { color: NAVY, fontSize: 22, fontWeight: 700 },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  cardHeader: { display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #f1f5f9' },
  cardTitle: { color: NAVY, fontSize: 13, fontWeight: 600 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '9px 14px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { color: '#374151', fontSize: 13, padding: '9px 14px', verticalAlign: 'middle' },
  statusPill: { borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 600 },
  primaryBtn: { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  cancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 13, cursor: 'pointer' },
  errorBox: { background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#dc2626', fontSize: 13, padding: '12px 16px', marginBottom: 16 },
  resultCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '48px 40px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  resultGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, maxWidth: 640, margin: '0 auto' },
  resultStat: { padding: '20px 16px', background: '#f8fafc', borderRadius: 10 },
};
